import { Injectable, inject } from '@angular/core';
import { BannerType, FeatureFlagsFacade, IMaintenanceWindow } from '@ra-state';
import { NotificationType } from '@ra-web-tech-ui-toolkit/components';
import { Banner } from '@rockwell-automation-inc/common-utils';
import { DateTime } from 'luxon';
import { Observable, tap } from 'rxjs';

const MAINTENANCE_WINDOW_PRE_WARNING_DAYS = 5;

// Shared maintenance-window banner logic - single source of truth for both ShellComponent and EulaComponent,
// since EulaComponent is a standalone route outside Shell.childRoutes and can't reuse Shell's tray state directly.
@Injectable({
  providedIn: 'root',
})
export class MaintenanceBannerService {
  private readonly featureFlagsFacade = inject(FeatureFlagsFacade);

  private appendCustomMessage(message: string, customMessage?: string): string {
    return customMessage ? `${message} ${customMessage}` : message;
  }

  private buildMaintenanceBanner(message: string, notificationType: NotificationType, bannerIcon: string): Banner {
    return {
      show: true,
      message: message,
      config: {
        showDefaultIcon: true,
        showCloseIcon: true,
        useNotificationBackground: true,
        notificationType: notificationType,
      },
      buttons: [],
      raIcon: bannerIcon,
      id: BannerType.MaintenanceWindow,
    };
  }

  maintenanceWindow$(
    showBanner: (banner: Banner) => void,
    closeBanner: (bannerType: BannerType) => void,
  ): Observable<IMaintenanceWindow> {
    return this.featureFlagsFacade.maintenanceWindow$.pipe(
      tap((maintenanceWindow) => {
        const start = maintenanceWindow.from.toLocaleString(DateTime.DATETIME_MED);
        const end = maintenanceWindow.to.toLocaleString(DateTime.DATETIME_MED);
        if (maintenanceWindow.isActive) {
          const msg = `Maintenance in progress: FactoryTalk Hub is going through scheduled maintenance until ${end}. The portal is currently in read-only mode.`;
          showBanner(
            this.buildMaintenanceBanner(
              this.appendCustomMessage(msg, maintenanceWindow.customMessage),
              NotificationType.Warning,
              'ra-icon-ide-sm-root-asset',
            ),
          );
        } else if (maintenanceWindow.startsWithinDays(MAINTENANCE_WINDOW_PRE_WARNING_DAYS)) {
          const msg = `Upcoming Scheduled Maintenance: ${start} to ${end}. FactoryTalk Hub portal will be available in read-only mode during this time. Access to other services will not be affected.`;
          showBanner(
            this.buildMaintenanceBanner(
              this.appendCustomMessage(msg, maintenanceWindow.customMessage),
              NotificationType.Info,
              'ra-icon-ide-sm-alarm',
            ),
          );
        } else {
          closeBanner(BannerType.MaintenanceWindow);
        }
      }),
    );
  }

  maintenanceWindowCompleted$(closeBanner: (bannerType: BannerType) => void): Observable<any> {
    return this.featureFlagsFacade.maintenanceWindowCompleted$.pipe(
      tap(() => closeBanner(BannerType.MaintenanceWindow)),
    );
  }
}
